const DashboardStats = ({ stats, onViewAssessments, role = 'admin' }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatScore = (score) => {
    return typeof score === 'number' ? score.toFixed(1) : '0.0'
  }

  const isReader = role === 'reader'

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {/* Total Assessments */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="p-2 bg-blue-100 rounded-lg">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">
              {isReader ? 'Vos évaluations' : 'Total évaluations'}
            </p>
            <p className="text-2xl font-bold text-gray-900">{stats.assessments.total}</p>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={onViewAssessments}
            className="text-blue-600 text-sm hover:text-blue-800"
          >
            {isReader ? 'Voir vos évaluations →' : 'Voir toutes →'}
          </button>
        </div>
      </div>

      {/* Average Score */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="p-2 bg-green-100 rounded-lg">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Score moyen</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatScore(stats.assessments.averageScore)}/10
            </p>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-500 h-2 rounded-full" 
              style={{ width: `${(stats.assessments.averageScore / 10) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Companies */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="p-2 bg-purple-100 rounded-lg">
            <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">
              {isReader ? 'Vos clients' : 'Entreprises'}
            </p>
            <p className="text-2xl font-bold text-gray-900">{stats.assessments.companies}</p>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-sm text-gray-500">
            {stats.assessments.companies > 0 && 
              `${(stats.assessments.total / stats.assessments.companies).toFixed(1)} éval./entreprise`
            }
          </p>
        </div>
      </div>

      {/* Date Range */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center">
          <div className="p-2 bg-orange-100 rounded-lg">
            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Période</p>
            {stats.assessments.dateRange ? (
              <div className="text-sm text-gray-900">
                <p>{formatDate(stats.assessments.dateRange.oldest)}</p>
                <p className="text-xs text-gray-500">
                  au {formatDate(stats.assessments.dateRange.newest)}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Aucune donnée</p>
            )}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg shadow p-6 lg:col-span-2">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Statut du système</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Sessions actives</p>
            <p className="text-xl font-bold text-green-600">{stats.system.activeSessions}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Heure serveur</p>
            <p className="text-sm text-gray-900">
              {new Date(stats.system.serverTime).toLocaleString('fr-FR')}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6 lg:col-span-2">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h3>
        <div className="space-y-3">
          <button
            onClick={onViewAssessments}
            className="w-full text-left px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
          >
            Consulter les évaluations récentes
          </button>
          <div className="text-sm text-gray-500">
            Dernière mise à jour: {new Date().toLocaleTimeString('fr-FR')}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardStats
